import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Shield, ShieldX, ShieldAlert, ShieldCheck, Trash2, RefreshCcw, Plus, ChevronDown, ChevronRight, Ban, UserCheck, Inbox, CheckCircle2, AlertTriangle, Users, Bell } from "lucide-react";
import VerifySetup from "@/components/VerifySetup";
import { GracePicker, GraceCell, graceLabel } from "@/components/blacklist/GracePicker";
import GraceCountdowns from "@/components/blacklist/GraceCountdowns";
import UserServers, { mergeGuilds } from "@/components/discord/UserServers";

/**
 * Admin panel tab: Discord server blacklist + linked-user roster with
 * expandable guild lists + ban activity log. Mounted inside Dashboard.js.
 */
export default function DiscordSecurity() {
  const [blacklist, setBlacklist] = useState([]);
  const [users, setUsers] = useState([]);
  const [banLog, setBanLog] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({ guild_id: "", name: "", reason: "", grace_hours: 24 });
  const [defaultGrace, setDefaultGrace] = useState(24);
  const [homeGuildId, setHomeGuildId] = useState("");
  const [graceKey, setGraceKey] = useState(0);
  const [adding, setAdding] = useState(false);
  const [whitelist, setWhitelist] = useState([]);
  const [wlForm, setWlForm] = useState({ discord_id: "", username: "", reason: "" });
  const [wlAdding, setWlAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null); // { last_synced_at, last_summary }
  const [refreshingGuilds, setRefreshingGuilds] = useState(false);
  const [guildStatus, setGuildStatus] = useState(null);
  const [blChannel, setBlChannel] = useState("");        // editable blacklist alerts channel id
  const [blChannelSaved, setBlChannelSaved] = useState(""); // persisted value
  const [savingChannel, setSavingChannel] = useState(false);
  const [userBlEnabled, setUserBlEnabled] = useState(true);
  const [userBlSince, setUserBlSince] = useState(null);
  const [togglingUserBl, setTogglingUserBl] = useState(false);

  const loadSyncStatus = async () => {
    try {
      const [sync, guild] = await Promise.all([
        api.get("/admin/sync-discord-members/status"),
        api.get("/admin/refresh-user-guilds/status"),
      ]);
      setSyncStatus(sync.data);
      setGuildStatus(guild.data);
    } catch { /* soft-fail */ }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-discord-members");
      const parts = [
        `${data.newly_created} new`,
        `${data.refreshed} refreshed`,
        `${data.skipped_bots} bots skipped`,
      ];
      if ((data.marked_inactive || 0) > 0) parts.push(`${data.marked_inactive} left server`);
      if ((data.restored || 0) > 0) parts.push(`${data.restored} rejoined`);
      toast.success(`Synced • ${parts.join(" · ")}`);
      await Promise.all([loadSyncStatus(), load()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const runGuildRefresh = async () => {
    setRefreshingGuilds(true);
    try {
      const { data } = await api.post("/admin/refresh-user-guilds");
      const oauth = data.refreshed_oauth ?? data.refreshed ?? 0;
      const probe = data.refreshed_probe ?? 0;
      const parts = [`${oauth} via OAuth`, `${probe} via bot probe`];
      if (data.banned) parts.push(`${data.banned} auto-banned`);
      if ((data.no_source ?? data.no_token ?? 0) > 0) parts.push(`${data.no_source ?? data.no_token} skipped`);
      toast.success(`Guild sync • ${parts.join(" · ")}`);
      await Promise.all([loadSyncStatus(), load()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshingGuilds(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [bl, us, log, ap, ss, wl] = await Promise.all([
        api.get("/admin/blacklist"),
        api.get("/admin/users-with-guilds"),
        api.get("/admin/ban-log"),
        api.get("/admin/ban-appeals"),
        api.get("/admin/site-settings"),
        api.get("/admin/whitelist"),
      ]);
      setBlacklist(bl.data);
      setUsers(us.data);
      setBanLog(log.data);
      setAppeals(ap.data);
      setWhitelist(wl.data);
      setBlChannel(ss.data.blacklist_channel_id || "");
      setBlChannelSaved(ss.data.blacklist_channel_id || "");
      const dg = ss.data.blacklist_default_grace_hours ?? 24;
      setDefaultGrace(dg);
      setHomeGuildId(ss.data.home_guild_id || "");
      setForm((f) => (f.guild_id ? f : { ...f, grace_hours: dg }));
      setGraceKey((k) => k + 1);
      setUserBlEnabled(ss.data.user_blacklist_enabled !== false);
      setUserBlSince(ss.data.user_blacklist_disabled_at || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  const toggleUserBl = async (next) => {
    setTogglingUserBl(true);
    try {
      const { data } = await api.put("/admin/site-settings", { user_blacklist_enabled: next });
      setUserBlEnabled(data.user_blacklist_enabled !== false);
      setUserBlSince(data.user_blacklist_disabled_at || null);
      toast.success(next ? "User blacklist enforcement resumed" : "User blacklist enforcement paused");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update");
    } finally { setTogglingUserBl(false); }
  };

  const saveBlChannel = async () => {
    setSavingChannel(true);
    try {
      const { data } = await api.put("/admin/site-settings", { blacklist_channel_id: blChannel.trim() });
      setBlChannelSaved(data.blacklist_channel_id || "");
      setBlChannel(data.blacklist_channel_id || "");
      toast.success(data.blacklist_channel_id ? "Blacklist alerts channel saved" : "Blacklist alerts channel cleared");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save channel");
    } finally { setSavingChannel(false); }
  };

  useEffect(() => { load(); loadSyncStatus(); }, []);

  const addEntry = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const { data } = await api.post("/admin/blacklist", form);
      toast.success(`Blacklisted • ${graceLabel(data.blacklist.grace_hours)} to leave • ${data.scan.newly_banned} newly banned of ${data.scan.scanned} scanned`);
      setForm({ guild_id: "", name: "", reason: "", grace_hours: defaultGrace });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add");
    } finally { setAdding(false); }
  };

  const removeEntry = async (gid) => {
    if (!window.confirm(`Remove ${gid} from the blacklist? Local ban flags on affected users will be cleared.`)) return;
    try {
      await api.delete(`/admin/blacklist/${gid}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const addWhitelist = async (e) => {
    e.preventDefault();
    setWlAdding(true);
    try {
      const { data } = await api.post("/admin/whitelist", wlForm);
      toast.success(data.unbanned ? `Whitelisted • lifted ban on ${data.unbanned} account(s)` : "Whitelisted — immune to all bans");
      setWlForm({ discord_id: "", username: "", reason: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to whitelist");
    } finally { setWlAdding(false); }
  };

  const removeWhitelist = async (did) => {
    if (!window.confirm(`Remove ${did} from the whitelist? They'll be subject to bans again.`)) return;
    try {
      await api.delete(`/admin/whitelist/${did}`);
      toast.success("Removed from whitelist");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };


  const rescan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/blacklist/scan");
      toast.success(`Scanned ${data.scanned} users → ${data.newly_banned} newly banned`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setScanning(false); }
  };

  const unban = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/unban`);
      toast.success("Local ban flag cleared");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const markAppealReviewed = async (id) => {
    try {
      const { data } = await api.post(`/admin/ban-appeals/${id}/review`, { decision: "rejected" });
      toast.success(data.notified ? "Marked reviewed — user DM'd" : "Marked reviewed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const unbanFromAppeal = async (userId, appealId) => {
    try {
      const { data } = await api.post(`/admin/ban-appeals/${appealId}/review`, { decision: "accepted" });
      toast.success(data.notified
        ? "Unbanned & user DM'd on Discord 🎉"
        : "Unbanned — DM to user failed (they may not share a server with the bot)");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const bulkDismissSpam = async () => {
    const spamCount = appeals.filter((a) => !a.reviewed && a.flagged_as_spam).length;
    if (!spamCount) { toast("No spam-flagged appeals to dismiss."); return; }
    if (!window.confirm(`Dismiss ${spamCount} spam-flagged appeal${spamCount === 1 ? "" : "s"}? They'll be marked rejected silently (no user DM).`)) return;
    try {
      const { data } = await api.post("/admin/ban-appeals/bulk-dismiss-spam");
      toast.success(`Dismissed ${data.dismissed} spam appeal${data.dismissed === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const blacklistIds = new Set(blacklist.map((b) => b.guild_id));

  return (
    <div className="space-y-10" data-testid="discord-security-tab">
      {/* Header + rescan */}
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow text-[var(--ff-red)] flex items-center gap-2 mb-2"><Shield size={12} /> ▮ DISCORD SECURITY</div>
          <h2 className="font-display text-3xl">Server Blacklist & Ban Enforcement</h2>
          <p className="text-xs text-[var(--ff-text-dim)] mt-1 max-w-2xl">
            When a linked user shares a server on the blacklist, they get banned from your Freshy Fresh Discord instantly and blocked from every fan feature. Manage from here or via <code className="text-white">/blacklist</code> slash commands in Discord.
          </p>
        </div>
        <button onClick={rescan} disabled={scanning} className="btn-primary text-xs" data-testid="rescan-btn">
          <RefreshCcw size={12} className={scanning ? "animate-spin" : ""} /> {scanning ? "Scanning…" : "Rescan All Users"}
        </button>
      </div>

      {/* OAuth-button Discord verification */}
      <VerifySetup />

      {/* Blacklist alerts channel */}
      <div className="surface-card p-6" data-testid="blacklist-alerts-channel-card">
        <div className="label-eyebrow text-[var(--ff-red)] flex items-center gap-2 mb-2"><Bell size={12} /> ▮ BLACKLIST ALERTS CHANNEL</div>
        <p className="text-xs text-[var(--ff-text-dim)] mb-3 max-w-2xl">
          Paste a Discord channel ID and the bot will post there for transparency — whenever a server or user is added to
          a blacklist, and whenever someone gets auto-caught by it (showing the user + which server triggered it). Leave
          blank to turn alerts off.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <input
            value={blChannel}
            onChange={(e) => setBlChannel(e.target.value)}
            maxLength={40}
            placeholder="Discord channel ID (e.g. 1234567890123456789)"
            className="flex-1 px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)] font-mono-ui"
            data-testid="blacklist-alerts-channel-input"
          />
          <button
            onClick={saveBlChannel}
            disabled={savingChannel || blChannel.trim() === blChannelSaved.trim()}
            className="btn-primary text-xs justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="blacklist-alerts-channel-save"
          >
            {savingChannel ? "Saving…" : "Save Channel"}
          </button>
        </div>
        <div className="mt-2 label-eyebrow" data-testid="blacklist-alerts-channel-state">
          {blChannelSaved
            ? <span className="text-emerald-400">● Alerts posting to #{blChannelSaved}</span>
            : <span className="text-[var(--ff-text-dim)]">○ No channel set — alerts are off</span>}
        </div>
      </div>

      {/* User Blacklist enforcement kill-switch */}
      <div className="surface-card p-6" data-testid="user-blacklist-killswitch-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <div className="label-eyebrow flex items-center gap-2 mb-2" style={{ color: userBlEnabled ? "var(--ff-text-dim)" : "var(--ff-red)" }}>
              <Ban size={12} /> ▮ USER BLACKLIST ENFORCEMENT
            </div>
            <p className="text-xs text-[var(--ff-text-dim)]">
              When ON, users on the User Blacklist — and anyone sharing a Discord server with them — are auto-banned.
              Turn OFF to pause the user-blacklist rules entirely. The <span className="text-white">server blacklist</span> above
              and <span className="text-white">manual moderator bans</span> keep working either way.
            </p>
            <div className="mt-3 label-eyebrow" data-testid="user-blacklist-killswitch-state">
              {userBlEnabled
                ? <span className="text-emerald-400">● Enforcement active</span>
                : <span className="text-[var(--ff-red)]">○ Paused{userBlSince ? ` — since ${new Date(userBlSince).toLocaleString()}` : ""}</span>}
            </div>
          </div>
          <button
            onClick={() => toggleUserBl(!userBlEnabled)}
            disabled={togglingUserBl}
            className={userBlEnabled
              ? "btn-ghost text-xs border border-[var(--ff-red)] text-[var(--ff-red)] disabled:opacity-40"
              : "btn-primary text-xs disabled:opacity-40"}
            data-testid="user-blacklist-killswitch-toggle"
          >
            {togglingUserBl ? "Saving…" : (userBlEnabled ? "Pause Enforcement" : "Resume Enforcement")}
          </button>
        </div>
      </div>

      {/* Discord Member Sync */}
      <div className="surface-card p-6" data-testid="discord-sync-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="label-eyebrow text-[var(--ff-red)] flex items-center gap-2 mb-2">
              <Users size={12} /> ▮ DISCORD MEMBER SYNC
            </div>
            <div className="font-display text-2xl mb-2">Pull existing members onto the site</div>
            <p className="text-xs text-[var(--ff-text-dim)] max-w-2xl">
              One-shot import of every human member of your Discord server into the website&apos;s user
              table. Auto-runs every 6 hours in the background too. Idempotent — running it again
              only refreshes usernames, avatars, and role mappings. Also handles{" "}
              <span className="text-white">departed-member cleanup</span>: users who left the server
              since last sync are soft-flagged inactive, demoted to <span className="font-mono-ui">user</span>,
              and hidden from the public team roster. If they rejoin, their previous role is
              auto-restored. Requires <span className="text-white font-mono-ui">SERVER MEMBERS INTENT</span>{" "}
              in the Discord Developer Portal.
            </p>
            {syncStatus?.last_summary && (
              <div className="mt-3 text-xs text-[var(--ff-text-dim)] font-mono-ui uppercase tracking-[0.15em] flex flex-wrap gap-4" data-testid="sync-last-summary">
                <span>Last: <span className="text-white">{syncStatus.last_synced_at ? new Date(syncStatus.last_synced_at).toLocaleString() : "never"}</span></span>
                <span>Scanned: <span className="text-white">{syncStatus.last_summary.total_scanned}</span></span>
                <span>New: <span className="text-emerald-400">{syncStatus.last_summary.newly_created}</span></span>
                <span>Refreshed: <span className="text-white">{syncStatus.last_summary.refreshed}</span></span>
                {(syncStatus.last_summary.marked_inactive || 0) > 0 && (
                  <span>Left server: <span className="text-[var(--ff-red)]">{syncStatus.last_summary.marked_inactive}</span></span>
                )}
                {(syncStatus.last_summary.restored || 0) > 0 && (
                  <span>Rejoined: <span className="text-emerald-400">{syncStatus.last_summary.restored}</span></span>
                )}
                <span>Bots: <span className="text-[var(--ff-text-dim)]">{syncStatus.last_summary.skipped_bots}</span></span>
                {syncStatus.last_summary.errors > 0 && (
                  <span>Errors: <span className="text-[var(--ff-red)]">{syncStatus.last_summary.errors}</span></span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={runSync}
            disabled={syncing}
            className="btn-primary text-xs shrink-0"
            data-testid="sync-discord-members-btn"
          >
            <Users size={12} className={syncing ? "animate-pulse" : ""} />{" "}
            {syncing ? "Syncing…" : "Sync Discord Members"}
          </button>
        </div>
      </div>

      {/* User Guild List Auto-Refresh */}
      <div className="surface-card p-6" data-testid="guild-refresh-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="label-eyebrow text-[var(--ff-red)] flex items-center gap-2 mb-2">
              <RefreshCcw size={12} /> ▮ USER GUILD AUTO-SYNC
            </div>
            <div className="font-display text-2xl mb-2">Keep everyone&apos;s server list fresh</div>
            <p className="text-xs text-[var(--ff-text-dim)] max-w-2xl">
              Refreshes each user&apos;s Discord server list and re-checks the blacklist. Uses two sources:
              (1) their stored <span className="text-white">OAuth token</span> when they&apos;ve signed in on the site
              (returns their <span className="text-white">full guild list</span>), and (2) a{" "}
              <span className="text-white">bot-side probe</span> when they haven&apos;t — the bot checks membership in every
              server it&apos;s a member of, so probe coverage grows as you invite the bot to more servers.
              Runs every <span className="text-white">24 hours</span>. Users who joined a blacklisted server get auto-banned instantly.
            </p>
            {guildStatus?.last_summary && (
              <div className="mt-3 text-xs text-[var(--ff-text-dim)] font-mono-ui uppercase tracking-[0.15em] flex flex-wrap gap-4" data-testid="guild-refresh-last-summary">
                <span>Last: <span className="text-white">{guildStatus.last_synced_at ? new Date(guildStatus.last_synced_at).toLocaleString() : "never"}</span></span>
                <span>Scanned: <span className="text-white">{guildStatus.last_summary.scanned}</span></span>
                <span>OAuth: <span className="text-emerald-400">{guildStatus.last_summary.refreshed_oauth ?? guildStatus.last_summary.refreshed ?? 0}</span></span>
                <span>Bot probe: <span className="text-emerald-400">{guildStatus.last_summary.refreshed_probe ?? 0}</span></span>
                {(guildStatus.last_summary.bot_guild_count ?? 0) > 0 && (
                  <span>Bot in: <span className="text-white">{guildStatus.last_summary.bot_guild_count} server{guildStatus.last_summary.bot_guild_count === 1 ? "" : "s"}</span></span>
                )}
                {guildStatus.last_summary.banned > 0 && (
                  <span>Auto-banned: <span className="text-[var(--ff-red)]">{guildStatus.last_summary.banned}</span></span>
                )}
                {(guildStatus.last_summary.no_source ?? guildStatus.last_summary.no_token ?? 0) > 0 && (
                  <span>Skipped: <span className="text-[var(--ff-text-dim)]">{guildStatus.last_summary.no_source ?? guildStatus.last_summary.no_token}</span></span>
                )}
                {guildStatus.last_summary.errors > 0 && (
                  <span>Errors: <span className="text-[var(--ff-red)]">{guildStatus.last_summary.errors}</span></span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={runGuildRefresh}
            disabled={refreshingGuilds}
            className="btn-primary text-xs shrink-0"
            data-testid="refresh-user-guilds-btn"
          >
            <RefreshCcw size={12} className={refreshingGuilds ? "animate-spin" : ""} />{" "}
            {refreshingGuilds ? "Refreshing…" : "Refresh Now"}
          </button>
        </div>
      </div>

      {/* Add blacklist entry */}
      <form onSubmit={addEntry} className="surface-card p-6 grid md:grid-cols-5 gap-3" data-testid="blacklist-add-form">
        <input
          required
          value={form.guild_id}
          onChange={(e) => setForm({ ...form, guild_id: e.target.value.replace(/\D/g, "") })}
          placeholder="Discord Server ID"
          className="px-3 py-2.5 font-mono-ui text-sm"
          data-testid="blacklist-guild-id-input"
        />
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Label (e.g. Toxic Server)"
          className="px-3 py-2.5 text-sm"
          data-testid="blacklist-name-input"
        />
        <input
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          placeholder="Reason (optional)"
          className="px-3 py-2.5 text-sm"
          data-testid="blacklist-reason-input"
        />
        <GracePicker key={`grace-${defaultGrace}`} value={form.grace_hours} onChange={(h) => setForm({ ...form, grace_hours: h })} testId="blacklist-grace-select" />
        <button disabled={adding} className="btn-primary text-xs justify-center" data-testid="blacklist-add-btn">
          <Plus size={12} /> {adding ? "Adding…" : "Blacklist & Scan"}
        </button>
      </form>

      {/* Blacklist table */}
      <section>
        <div className="label-eyebrow mb-3 flex items-center gap-2 text-[var(--ff-red)]"><ShieldX size={12} /> ▮ BLACKLISTED SERVERS · {blacklist.length}</div>
        {blacklist.length === 0 ? (
          <div className="text-sm text-[var(--ff-text-dim)] font-mono-ui">No servers on the blacklist yet.</div>
        ) : (
          <div className="border border-[var(--ff-border)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--ff-surface)] font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                  <th className="text-left px-4 py-3">Server ID</th>
                  <th className="text-left px-4 py-3">Label</th>
                  <th className="text-left px-4 py-3">Reason</th>
                  <th className="text-left px-4 py-3">Time to leave</th>
                  <th className="text-left px-4 py-3">Added</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {blacklist.map((b) => (
                  <tr key={b.guild_id} className="border-t border-[var(--ff-border)]" data-testid={`blacklist-row-${b.guild_id}`}>
                    <td className="px-4 py-3 font-mono-ui text-xs">{b.guild_id}</td>
                    <td className="px-4 py-3">{b.name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[var(--ff-text-dim)]">{b.reason || "—"}</td>
                    <td className="px-4 py-3"><GraceCell key={`${b.guild_id}-${b.grace_hours}`} entry={b} onSaved={load} /></td>
                    <td className="px-4 py-3 text-xs text-[var(--ff-text-dim)]">{(b.added_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => removeEntry(b.guild_id)} className="text-[var(--ff-red)] hover:text-white" title="Remove" data-testid={`blacklist-remove-${b.guild_id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <GraceCountdowns reloadKey={graceKey} onDefaultSaved={(h) => { setDefaultGrace(h); setForm((f) => ({ ...f, grace_hours: h })); }} />

      {/* Whitelist — immunity to all bans */}
      <form onSubmit={addWhitelist} className="surface-card p-6 grid md:grid-cols-4 gap-3 border-emerald-500/30" data-testid="whitelist-add-form">
        <div className="md:col-span-4 label-eyebrow flex items-center gap-2 text-emerald-400 mb-1">
          <ShieldCheck size={12} /> ▮ WHITELIST · IMMUNITY TO ALL BANS
        </div>
        <p className="md:col-span-4 text-xs text-[var(--ff-text-dim)] -mt-1 mb-1">
          Whitelisted Discord users can never be banned — not by the auto blacklist/adjacency rule, manual moderator bans,
          the Community Lounge bridge, or Discord-sync. Adding someone who's currently banned unbans them instantly.
        </p>
        <input
          required
          value={wlForm.discord_id}
          onChange={(e) => setWlForm({ ...wlForm, discord_id: e.target.value.replace(/\D/g, "") })}
          placeholder="Discord User ID"
          className="px-3 py-2.5 font-mono-ui text-sm"
          data-testid="whitelist-discord-id-input"
        />
        <input
          value={wlForm.username}
          onChange={(e) => setWlForm({ ...wlForm, username: e.target.value })}
          placeholder="Username (e.g. bigfan)"
          className="px-3 py-2.5 text-sm"
          data-testid="whitelist-username-input"
        />
        <input
          value={wlForm.reason}
          onChange={(e) => setWlForm({ ...wlForm, reason: e.target.value })}
          placeholder="Reason (optional)"
          className="px-3 py-2.5 text-sm"
          data-testid="whitelist-reason-input"
        />
        <button disabled={wlAdding} className="btn-primary text-xs justify-center" data-testid="whitelist-add-btn">
          <ShieldCheck size={12} /> {wlAdding ? "Adding…" : "Grant Immunity"}
        </button>
      </form>

      <section>
        <div className="label-eyebrow mb-3 flex items-center gap-2 text-emerald-400"><ShieldCheck size={12} /> ▮ BAN-IMMUNE USERS · {whitelist.length}</div>
        {whitelist.length === 0 ? (
          <div className="text-sm text-[var(--ff-text-dim)] font-mono-ui">No one is whitelisted yet.</div>
        ) : (
          <div className="border border-emerald-500/30">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--ff-surface)] font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                  <th className="text-left px-4 py-3">User ID</th>
                  <th className="text-left px-4 py-3">Username</th>
                  <th className="text-left px-4 py-3">Reason</th>
                  <th className="text-left px-4 py-3">Added</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {whitelist.map((w) => (
                  <tr key={w.discord_id} className="border-t border-[var(--ff-border)]" data-testid={`whitelist-row-${w.discord_id}`}>
                    <td className="px-4 py-3 font-mono-ui text-xs">{w.discord_id}</td>
                    <td className="px-4 py-3">{w.username || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[var(--ff-text-dim)]">{w.reason || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[var(--ff-text-dim)]">{(w.added_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => removeWhitelist(w.discord_id)} className="text-[var(--ff-red)] hover:text-white" title="Remove immunity" data-testid={`whitelist-remove-${w.discord_id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div className="label-eyebrow mb-3 flex items-center gap-2"><ShieldAlert size={12} /> ▮ LINKED DISCORD USERS · {users.length}</div>
        {loading ? (
          <div className="text-sm text-[var(--ff-text-dim)]">Loading…</div>
        ) : (
          <div className="space-y-2">
            {users.map((u) => {
              const guilds = u.discord_guilds || [];
              const bad = guilds.filter((g) => blacklistIds.has(g.id));
              const allCount = mergeGuilds(guilds, u.discord_guilds_full).length;
              const isOpen = !!expanded[u.discord_id];
              return (
                <div key={u.discord_id} className="border border-[var(--ff-border)]" data-testid={`user-row-${u.discord_id}`}>
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--ff-surface)] text-left"
                    onClick={() => setExpanded({ ...expanded, [u.discord_id]: !isOpen })}
                    data-testid={`user-toggle-${u.discord_id}`}
                  >
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    {u.discord_avatar
                      ? <img src={u.discord_avatar} alt="" className="w-8 h-8 rounded-full" />
                      : <div className="w-8 h-8 bg-[var(--ff-red)] rounded-full flex items-center justify-center text-white text-xs font-display">{u.discord_username?.[0] || "?"}</div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-ui text-sm text-white truncate">{u.discord_username}</div>
                      <div className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] uppercase tracking-[0.15em]">
                        {(u.roles || []).join(" · ") || "user"} · {allCount} server{allCount === 1 ? "" : "s"}
                      </div>
                    </div>
                    {u.is_banned && (
                      <span className="px-2 py-0.5 border border-[var(--ff-red)] text-[var(--ff-red)] font-mono-ui text-[0.55rem] uppercase tracking-[0.15em] flex items-center gap-1">
                        <Ban size={10} /> Banned
                      </span>
                    )}
                    {bad.length > 0 && !u.is_banned && (
                      <span className="px-2 py-0.5 border border-yellow-500 text-yellow-500 font-mono-ui text-[0.55rem] uppercase tracking-[0.15em]">
                        {bad.length} hit
                      </span>
                    )}
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-[var(--ff-border)] bg-[var(--ff-surface)]/40" data-testid={`user-guilds-${u.discord_id}`}>
                      {u.is_banned && (
                        <div className="mt-3 p-3 border border-[var(--ff-red)] flex items-center justify-between gap-3">
                          <div className="text-xs">
                            <div className="text-[var(--ff-red)] font-mono-ui uppercase tracking-[0.15em] text-[0.6rem]">Banned</div>
                            <div className="text-[var(--ff-text-dim)] mt-1">{u.ban_reason || "—"}</div>
                          </div>
                          <button onClick={() => unban(u.id)} className="btn-ghost text-[0.65rem] flex-shrink-0" data-testid={`unban-${u.id}`}>
                            <UserCheck size={12} /> Clear ban flag
                          </button>
                        </div>
                      )}
                      {guilds.length === 0 && !(u.discord_guilds_full || []).length ? (
                        <p className="text-xs text-[var(--ff-text-dim)] font-mono-ui mt-3">
                          No guilds on file — user needs to sign in again to grant the guilds scope.
                        </p>
                      ) : (
                        <div className="mt-3">
                          <UserServers guilds={guilds} fullGuilds={u.discord_guilds_full || []} fullAt={u.discord_guilds_full_at} blacklistIds={blacklistIds} homeGuildId={homeGuildId} defaultGrace={defaultGrace}
                            ownerName={u.discord_username} onBlacklisted={load} syncedAt={u.last_guild_sync_at} syncSource={u.last_guild_sync_source}
                            testPrefix={`sec-${u.discord_id}`} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {users.length === 0 && <div className="text-sm text-[var(--ff-text-dim)] font-mono-ui">No Discord-linked users yet.</div>}
          </div>
        )}
      </section>

      {/* Ban appeals */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow flex items-center gap-2 text-[#FFD60A]">
            <Inbox size={12} /> ▮ BAN APPEALS · {appeals.filter((a) => !a.reviewed).length} PENDING
            {appeals.length > 0 && <span className="text-[var(--ff-text-dim)]">({appeals.length} total)</span>}
          </div>
          {appeals.filter((a) => !a.reviewed && a.flagged_as_spam).length > 0 && (
            <button onClick={bulkDismissSpam} className="btn-ghost text-xs border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black" data-testid="bulk-dismiss-spam-btn">
              <AlertTriangle size={12} /> Dismiss {appeals.filter((a) => !a.reviewed && a.flagged_as_spam).length} Spam
            </button>
          )}
        </div>
        {appeals.length === 0 ? (
          <div className="text-sm text-[var(--ff-text-dim)] font-mono-ui">No appeals submitted yet.</div>
        ) : (
          <div className="space-y-2">
            {appeals.map((a) => (
              <div key={a.id}
                   className={`border p-4 ${
                     a.reviewed ? "border-[var(--ff-border)] opacity-60" :
                     a.flagged_as_spam ? "border-yellow-500 bg-yellow-500/5" :
                     "border-[#FFD60A]"
                   }`}
                   data-testid={`appeal-${a.id}`}>
                <div className="flex items-start gap-3">
                  {a.discord_avatar
                    ? <img src={a.discord_avatar} alt="" className="w-10 h-10 rounded-full flex-shrink-0" />
                    : <div className="w-10 h-10 bg-[var(--ff-red)] rounded-full flex items-center justify-center text-white text-sm font-display flex-shrink-0">{(a.discord_username || "?")[0]}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono-ui text-sm text-white">{a.discord_username}</span>
                      <span className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] uppercase tracking-[0.15em]">
                        {(a.submitted_at || "").replace("T", " ").slice(0, 16)}
                      </span>
                      {a.flagged_as_spam && !a.reviewed && (
                        <span className="px-2 py-0.5 border border-yellow-500 text-yellow-500 font-mono-ui text-[0.55rem] uppercase tracking-[0.15em] flex items-center gap-1" title={a.spam_reason}>
                          <AlertTriangle size={9} /> Auto-flagged
                        </span>
                      )}
                      {a.reviewed && (
                        <span className={`px-2 py-0.5 border font-mono-ui text-[0.55rem] uppercase tracking-[0.15em] ${
                          a.decision === "accepted" ? "border-[#32D74B] text-[#32D74B]" : "border-[var(--ff-text-dim)] text-[var(--ff-text-dim)]"
                        }`}>
                          {a.decision || "reviewed"}
                        </span>
                      )}
                      {!a.notified && !a.reviewed && (
                        <span className="px-2 py-0.5 border border-[var(--ff-text-dim)] text-[var(--ff-text-dim)] font-mono-ui text-[0.55rem] uppercase tracking-[0.15em]" title="DM to admin failed — check Discord bot config">DM not sent</span>
                      )}
                    </div>
                    <div className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] uppercase tracking-[0.15em] mt-1">
                      Banned for: {a.ban_triggering_guild_name || a.ban_reason || "—"}
                    </div>
                    {a.flagged_as_spam && a.spam_reason && (
                      <div className="mt-2 text-[0.65rem] text-yellow-500 font-mono-ui">⚠ {a.spam_reason}</div>
                    )}
                    <blockquote className="mt-3 border-l-2 border-[var(--ff-border)] pl-3 text-sm text-white whitespace-pre-wrap font-mono-ui leading-relaxed" data-testid={`appeal-msg-${a.id}`}>
                      {a.message}
                    </blockquote>
                    {!a.reviewed && (
                      <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => unbanFromAppeal(a.user_id, a.id)}
                          className="btn-primary text-[0.65rem]"
                          data-testid={`appeal-accept-${a.id}`}
                        >
                          <UserCheck size={12} /> Accept & Unban
                        </button>
                        <button
                          onClick={() => markAppealReviewed(a.id)}
                          className="btn-ghost text-[0.65rem]"
                          data-testid={`appeal-reject-${a.id}`}
                        >
                          <CheckCircle2 size={12} /> Mark Reviewed (Keep Ban)
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Ban log */}
      <section>
        <div className="label-eyebrow mb-3 flex items-center gap-2"><Ban size={12} /> ▮ BAN ACTIVITY · {banLog.length}</div>
        {banLog.length === 0 ? (
          <div className="text-sm text-[var(--ff-text-dim)] font-mono-ui">No bans yet.</div>
        ) : (
          <div className="border border-[var(--ff-border)] max-h-96 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[var(--ff-surface)]">
                <tr className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Trigger</th>
                  <th className="text-left px-4 py-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {banLog.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--ff-border)]">
                    <td className="px-4 py-2 text-xs text-[var(--ff-text-dim)] font-mono-ui">{(r.at || "").replace("T", " ").slice(0, 16)}</td>
                    <td className="px-4 py-2 text-xs">{r.discord_username || r.discord_id}</td>
                    <td className="px-4 py-2 text-xs font-mono-ui">{r.triggering_guild_name || r.triggering_guild_id}</td>
                    <td className="px-4 py-2 text-xs text-[var(--ff-text-dim)]">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
